import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

export default function ConsentTemplatesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [items, setItems] = useState([]);
  const [treatment, setTreatment] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  // Catalog of treatments from Profile (normalize strings/objects to names)
  const catalog = (user?.treatments || []).map((x) => typeof x === "string" ? x : x.name).filter(Boolean);

  const load = async () => { const { data } = await api.get("/consent-templates"); setItems(data); };
  useEffect(() => { load(); }, []);

  async function onFile(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (!treatment.trim()) { toast.error(t("templates.indicateTreatment")); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/consent-templates?treatment=${encodeURIComponent(treatment)}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("templates.uploaded")); setTreatment(""); setCustomMode(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function del(id) {
    if (!window.confirm(t("templates.confirmDelete"))) return;
    await api.delete(`/consent-templates/${id}`); load();
  }

  return (
    <div className="space-y-8 fade-in-up">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("templates.eyebrow")}</p>
        <h1 className="font-heading text-5xl font-light">{t("templates.title")}</h1>
        <p className="text-stone-500 mt-3 max-w-xl">{t("templates.description")}</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("templates.treatment")} *</label>
          {catalog.length > 0 && !customMode ? (
            <div className="flex gap-2">
              <select value={treatment} onChange={(e) => {
                if (e.target.value === "__custom__") { setCustomMode(true); setTreatment(""); }
                else setTreatment(e.target.value);
              }}
                data-testid="template-treatment-select"
                className="flex-1 px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                <option value="">{t("templates.selectTreatment") || "— Seleziona trattamento —"}</option>
                {catalog.map((n) => <option key={n} value={n}>{n}</option>)}
                <option value="__custom__">✎ {t("templates.other") || "Altro (scrivi manualmente)"}</option>
              </select>
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={treatment} onChange={(e) => setTreatment(e.target.value)}
                data-testid="template-treatment"
                placeholder={t("templates.treatmentPh")}
                className="flex-1 px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
              {catalog.length > 0 && (
                <button type="button" onClick={() => { setCustomMode(false); setTreatment(""); }}
                  className="px-3 py-2.5 text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900 border border-stone-200 rounded-xl">
                  ← {t("common.back") || "Indietro"}
                </button>
              )}
            </div>
          )}
          {catalog.length === 0 && (
            <p className="text-[11px] text-stone-400 mt-1.5">{t("templates.noCatalogHint") || "Puoi aggiungere trattamenti in Dati professionali → I tuoi trattamenti."}</p>
          )}
        </div>
        <input ref={inputRef} type="file" accept="application/pdf" onChange={onFile} className="hidden" />
        <button onClick={() => inputRef.current.click()} disabled={uploading || !treatment.trim()} data-testid="upload-template-btn"
          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Upload className="w-4 h-4" /> {uploading ? t("patientDetail.uploading") : t("templates.uploadPdf")}
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        {items.length === 0 ? (
          <div className="py-16 text-center text-stone-500">
            <FileText className="w-8 h-8 mx-auto mb-3 text-stone-300" strokeWidth={1} />
            <p className="text-sm">{t("templates.empty")}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                <th className="text-left px-6 py-4">{t("templates.treatment")}</th>
                <th className="text-left px-6 py-4">{t("templates.colFile")}</th>
                <th className="text-left px-6 py-4 hidden md:table-cell">{t("templates.colUploaded")}</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((tpl) => (
                <tr key={tpl.id} data-testid={`template-${tpl.id}`} className="border-b border-stone-50">
                  <td className="px-6 py-4 font-medium">{tpl.treatment || "—"}</td>
                  <td className="px-6 py-4 text-sm text-stone-600 truncate max-w-[300px]">{tpl.original_filename}</td>
                  <td className="px-6 py-4 text-sm text-stone-500 hidden md:table-cell">{new Date(tpl.created_at).toLocaleDateString(locale)}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => del(tpl.id)} className="text-stone-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
